import React from "react";
import { InboxIcon } from "lucide-react";
import { useCampaignStore, useCreatorsStore, useInboxStore } from "@/stores";
import EmptyState from "@/components/ui/empty-state";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@clerk/react";
import InboxAlerts from "./InboxAlerts";
import InboxMessage from "./InboxMessage";
import NextMessagePreview from "./NextMessagePreview";
import InboxFooter from "./InboxFooter";
import { CreatorInboxSkeleton } from "./CreatorInboxSkeleton";

interface CreatorInboxProps {
  creator_id: string | undefined;
  /** Scrolls with the thread, above the subject — e.g. phase breadcrumbs. */
  header?: React.ReactNode;
  /**
   * Amy-run campaign → the thread is read from Amy's pinned mailbox. Defaults
   * to the open campaign's flag; pass it where no campaign is open (org-wide CRM).
   */
  isEve?: boolean;
}

export default function CreatorInbox({ creator_id, header, isEve }: CreatorInboxProps) {
  const { creators } = useCreatorsStore();
  const { campaign } = useCampaignStore();
  const readViaAmy = isEve ?? campaign?.is_eve === true;
  const { fetchInbox, inbox, isLoading, setActiveCreatorName } = useInboxStore();
  const { getToken } = useAuth();

  const currentCreator = creators.find((c) => c._id === creator_id);
  const threadSubject = inbox.length > 0 ? inbox[0].subject : "Inbox";

  React.useEffect(() => {
    if (!creator_id) return;
    (async () => {
      try {
        const token = await getToken();
        if (!token) {
          console.error("Error: Token is null");
          return;
        }
        await fetchInbox(creator_id, token, { isEve: readViaAmy });
        setActiveCreatorName(
          creators.find((c) => c._id === creator_id)?.username || ""
        );
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    })();
  }, [creator_id, getToken, readViaAmy]);

  if (isLoading) {
    return (
      <div className="flex-1 justify-between bg-background h-full relative">
        <ScrollArea className="flex flex-col gap-5 w-full h-full bg-background z-10">
          {header}
          <CreatorInboxSkeleton />
        </ScrollArea>
      </div>
    );
  }

  if (!creator_id) {
    return (
      <div className="flex-1 justify-between bg-background h-full relative">
        <div className="flex items-center justify-center h-full">
          <EmptyState
            icon={<InboxIcon />}
            text="Select a creator to view their inbox"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 justify-between bg-background h-full relative animate-in fade-in duration-200">
      <ScrollArea className="flex flex-col gap-5 w-full h-full bg-background z-10">
        <div className="sticky top-0 z-50 bg-chrome">
          <InboxAlerts creator_id={creator_id} />
        </div>

        {header}

        <div className="py-2 px-3 text-sm font-medium text-foreground/90 flex flex-col border-b border-dashed">
          <span className="text-xs text-muted-foreground font-normal">Subject: </span>
          {threadSubject}
        </div>
        <div className="divide-y">
          {inbox.map((message, index: number) => (
            <InboxMessage
              key={index}
              message={message}
              creator_id={creator_id}
            />
          ))}
        </div>

        {currentCreator?.next_message && (
          <div className="mb-13 w-auto h-auto border-b">
            <NextMessagePreview
              creator_id={creator_id}
              nextMessage={currentCreator.next_message}
            />
          </div>
        )}

        <InboxFooter creator_id={creator_id} isEve={readViaAmy} />
      </ScrollArea>
    </div>
  );
}
